import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident, getCurrentYear} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import moment from 'moment'
import {MONTH} from '../../../../../CodeMaster'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {ApplyButton, ItemHeading, WideWhiteArea} from '../../common/resident/NatResidentApplicationsCommon'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {NatResidentBikeParkingUseWithdrawalApplyLocationState} from '../NatResidentApplicationInterface'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import {HKConstants} from '../../../../../HKConstants'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import {StyledDiv} from '../../../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import ErrorMessageAreaWrapper from '../../../../../components/shared/forms/resident/ErrorMessageAreaWrapper'
import {NatPutBikeParkingUseWithdrawalApplyRequest} from '../../repository/NatPutBikeParkingUseWithdrawalApply'
import PPSelectForResident, {Option} from '../../../../../components/shared/forms/resident/PPSelectForResident'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatResidentBikeParkingUseWithdrawalApplyLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    withdrawalArea: Nullable<string>
    withdrawalDate: Nullable<string>
    bikeCarryingOutDate: Nullable<string>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPutBikeParkingUseWithdrawalApplyRequest) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    withdrawalArea: string
    withdrawalYear: string
    withdrawalMonth: string
    bikeCarryingOutDate: string

    currentDate: moment.Moment
    holidays: string[]
    yearOptions: Option[]
    monthOptions: Option[]

    clearWithdrawalDateError: boolean
    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

export default class NatResidentBikeParkingUseWithdrawalApplyForm extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applyButtonDisabled: true,
            applicantFamilyName: '',
            applicantFirstName: '',
            withdrawalArea: '',
            withdrawalYear: '',
            withdrawalMonth: '',
            bikeCarryingOutDate: '',
            currentDate: moment(),
            holidays: [],
            yearOptions: [],
            monthOptions: [],
            clearWithdrawalDateError: false,
            isLoadCompleted: false,
        }

        this.handleChangeWithdrawalYear = this.handleChangeWithdrawalYear.bind(this)
        this.handleChangeWithdrawalMonth = this.handleChangeWithdrawalMonth.bind(this)
        this.handleChangebikeCarryingOutDate = this.handleChangebikeCarryingOutDate.bind(this)
        this.handleChangeText = this.handleChangeText.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])
            // 現在日時取得
            if (currentTimeResponse && currentTimeResponse.details) {
                const currentYear: number = getCurrentYear(`${currentTimeResponse.details.currentDateTime}`)
                const yearOptions: Option[] = [{label: '', value: ''}]
                const monthOptions: Option[] = [{label: '', value: ''}]

                let year: number = currentYear + 3
                for (let index: number = 1; index <= 7; index++) {
                    yearOptions.push({label: `${year}`, value: `${year}`})
                    year--
                }

                MONTH.values().map((month) => {
                    return (
                        monthOptions.push({
                            label: `${month.label}`, value: (String(month.value).length === 1)
                                ? '0' + String(month.value) : String(month.value),
                        })
                    )
                })
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                    yearOptions: yearOptions,
                    monthOptions: monthOptions,
                })
            }

            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const withdrawalArea = this.props.withdrawalArea ?? this.state.withdrawalArea
            const withdrawalDate = this.props.withdrawalDate ? this.props.withdrawalDate.split('-') : []
            const bikeCarryingOutDate = this.props.bikeCarryingOutDate ?? this.state.bikeCarryingOutDate

            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                withdrawalArea: withdrawalArea,
                withdrawalYear: withdrawalDate[0] ?? this.state.withdrawalYear,
                withdrawalMonth: withdrawalDate[1] ?? this.state.withdrawalMonth,
                bikeCarryingOutDate: bikeCarryingOutDate,
                holidays: holidays === null ? [] : holidays,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
            this.props.eventEmitter.closeLoading()
        }
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeWithdrawalYear(event: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({withdrawalYear: event.target.value, clearWithdrawalDateError: true}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeWithdrawalMonth(event: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({withdrawalMonth: event.target.value, clearWithdrawalDateError: true}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangebikeCarryingOutDate(date: string): void {
        this.setState({bikeCarryingOutDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, withdrawalArea,
            withdrawalYear, withdrawalMonth, bikeCarryingOutDate,
        } = this.state

        let applyButtonDisabled = false

        if (!applicantFamilyName || !applicantFirstName || !withdrawalArea || !withdrawalYear ||
            !withdrawalMonth || !bikeCarryingOutDate) {
            applyButtonDisabled = true
        }

        this.setState({applyButtonDisabled: applyButtonDisabled})
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, withdrawalArea,
            withdrawalYear, withdrawalMonth, bikeCarryingOutDate,
        } = this.state

        this.setState({clearWithdrawalDateError: false})

        const date = withdrawalYear + '-' + withdrawalMonth

        const request = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            withdrawalArea: withdrawalArea,
            withdrawalDate: date,
            bikeCarryingOutDate: bikeCarryingOutDate,
            confirmed: false,
        }

        if (!request) {
            return
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state

        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER),
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        onChange: this.handleChangeText,
                                        placeholder: '例：素敵',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        value: applicantFirstName,
                                        placeholder: '例：太郎',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderUseAreaForm(): ReactElement {
        const {withdrawalArea} = this.state

        return (
            <>
                <ItemHeading>
                    解約する区画
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>バイク置場の<BreakPcOnly/>区画番号</span>}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 20,
                                    name: 'withdrawalArea',
                                    value: withdrawalArea,
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['withdrawalArea']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderWithdrawalDateForm(): ReactElement {
        const {withdrawalMonth, withdrawalYear} = this.state
        return (
            <>
                <ItemHeading>
                    解約日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>バイク置場の契約を<BreakPcOnly/>解約する日付</span>}
                        required
                        component={
                            <ErrorMessageAreaWrapper>
                                <DisplayFlex>
                                    <UnitBoxArea>
                                        <PPSelectForResident
                                            inputProps={{
                                                value: withdrawalYear,
                                            }}
                                            placeholder={'年'}
                                            onChange={this.handleChangeWithdrawalYear}
                                            options={this.state.yearOptions}
                                            eventKeys={[`withdrawalDate`]}
                                            eventEmitter={this.props.eventEmitter}
                                            clearError={this.state.clearWithdrawalDateError}
                                        />
                                    </UnitBoxArea>
                                    <FlexContainer>
                                        <BlockSpacer width={5}/>
                                    </FlexContainer>
                                    <UnitBoxArea>
                                        <PPSelectForResident
                                            inputProps={{
                                                value: withdrawalMonth,
                                            }}
                                            placeholder={'月'}
                                            onChange={this.handleChangeWithdrawalMonth}
                                            options={this.state.monthOptions}
                                            eventKeys={[`withdrawalDate`]}
                                            eventEmitter={this.props.eventEmitter}
                                            messageInvisible
                                            clearError={this.state.clearWithdrawalDateError}
                                        />
                                    </UnitBoxArea>
                                    <FlexContainer>
                                        <BlockSpacer width={8}/>
                                    </FlexContainer>
                                    <StringFlexDiv>末日</StringFlexDiv>
                                </DisplayFlex>
                            </ErrorMessageAreaWrapper>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderbikeCarryingOutDateForm(): ReactElement {
        const {bikeCarryingOutDate, holidays} = this.state

        return (
            <>
                <ItemHeading>
                    バイク搬出日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>バイク置場から<BreakPcOnly/>バイクを搬出する日付</span>}
                        required
                        component={
                            <PPDatePickerForResident
                                date={bikeCarryingOutDate}
                                onChange={this.handleChangebikeCarryingOutDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['bikeCarryingOutDate']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderApplicantForm()}
                < Box height={'15px'}/>
                {this.renderUseAreaForm()}
                <Box height={'15px'}/>
                {this.renderWithdrawalDateForm()}
                <Box height={'15px'}/>
                {this.renderbikeCarryingOutDateForm()}
                <Box height={'15px'}/>
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`

const UnitBoxArea: StyledDiv = styled.div`
  width: 150px;
  ${currentMedia.greaterThan('pc')`
    width: 165px;
  `};
`

const DisplayFlex: StyledDiv = styled.div`
  display: flex;
`

const StringFlexDiv: StyledDiv = styled.div`
  display: flex;
  max-height: 50px;
  align-items: flex-end;
`

